import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { LocationModel } from '@app/modules/location/models/location.model';
import { environment } from '@env/environment';
import _ from 'lodash';
import { HttpService } from '@app/core/http/http.service';
import { InspectionType } from '@app/core/models/inspection-type.model';

// Import RxJs required methods
@Injectable({ providedIn: 'root' })
export class LocationService {
  private http = inject(HttpService);

  private locationStorageKey = 'previous_location';
  private _currentLocationSubject = new BehaviorSubject<LocationModel>(null);
  private _currentLocation: LocationModel;

  public get currentLocationSubject(): BehaviorSubject<LocationModel> {
    return this._currentLocationSubject;
  }

  public get currentLocation(): LocationModel {
    return this._currentLocation;
  }

  public set currentLocation(value: LocationModel) {
    this._currentLocation = value;
    this.http.locationId = _.get(value, 'locationId') as string;
    localStorage.setItem(this.locationStorageKey, JSON.stringify(value));
    this.currentLocationSubject.next(value);
  }

  constructor() {
    this._currentLocationSubject.subscribe();
    const localStorageLocationString = localStorage.getItem(this.locationStorageKey);
    if (localStorageLocationString) {
      this.currentLocation = JSON.parse(localStorageLocationString);
    }
  }

  /**
   * Get location by Id
   *
   * @param {string} locationId
   * @returns {Observable<LocationModel>}
   * @memberof LocationService
   */
  public getLocationById(locationId: string): Observable<LocationModel> {
    return this.http.get(environment.apiEndpoint + 'locations/' + locationId).pipe(
      map(res => (<any>res.body).data),
      catchError((error: any) => throwError(() => error.json().error || 'Server error')));
  }

  public getInspectionTypesByLocationId(locationId: string): Observable<InspectionType> {
    return this.http.get(environment.apiEndpoint + 'locations/' + locationId + '/inspection-types').pipe(
      map(res => (<any>res.body).data),
      catchError((error: any) => throwError(() => error.json().error || 'Server error')));
  }

  /**
   * Get all locations
   *
   * @returns {Observable<LocationModel[]>}
   * @memberof LocationService
   */
  public getAll(location: { lat: string, lng: string } = null): Observable<LocationModel[]> {

    const params = new HttpParams({
      fromObject: {
        ...location
      }
    });

    const url = location ? environment.apiEndpoint + 'locations?' + params.toString()
      : environment.apiEndpoint + 'locations';

    return this.http.get(url).pipe(
      map(res => {
        const locs = (<any>res.body).data as LocationModel[];

        if (locs) {
          if (this.currentLocation != null) {
            const indexOfCurrentLocation = locs.findIndex(i => i.locationId === this.currentLocation.locationId);
            const element = locs[indexOfCurrentLocation];
            locs.splice(indexOfCurrentLocation, 1);
            locs.splice(0, 0, element);
          }
        }
        return locs.filter(function (el) {
          return el !== null && el !== undefined;
        });
      }),
      catchError((error: any) => throwError(() => error.json().error || 'Server error')));
  }

  /**
   * Create location
   *
   * @param {LocationModel} location
   * @returns {Observable<any>}
   * @memberof LocationService
   */
  public createLocation(location: LocationModel): Observable<any> {
    return this.http.post(environment.apiEndpoint + 'locations', location).pipe(
      catchError((error: any) => throwError(() => error.json().error || 'Server error')));
  }

  /**
   * Edit location
   *
   * @param {LocationModel} location
   * @returns {Observable<any>}
   * @memberof LocationService
   */
  public editLocation(location: LocationModel): Observable<any> {
    return this.http.patch(environment.apiEndpoint + 'locations', location).pipe(
      catchError((error: any) => throwError(() => error.json().error || 'Server error')));
  }

  /**
   * Delete location
   *
   * @param {string} locationId
   * @returns {Observable<any>}
   * @memberof LocationService
   */
  public delete(locationId: string): Observable<any> {
    return this.http.delete(environment.apiEndpoint + 'locations/' + locationId).pipe(
      catchError((error: any) => throwError(() => error.json().error || 'Server error')));
  }

  /**
   * Filter locations by name
   *
   * @param {*} keyword
   * @param {LocationModel[]} locations
   * @returns {LocationModel[]}
   * @memberof LocationService
   */
  public filterLocationsByName(keyword: any, locations: LocationModel[]): LocationModel[] {
    if (keyword && _.isString(keyword)) {
      return locations.filter(s => s.name.toLowerCase().indexOf(keyword.toLowerCase()) === 0);
    }
    return locations;
  }


  /**
   * Get location selected from name
   *
   * @param {string} locationName
   * @param {LocationModel[]} locations
   * @returns {LocationModel}
   * @memberof LocationService
   */
  public getLocationSelectedFromName(locationName: string, locations: LocationModel[]): LocationModel {
    return _.find(locations, p => {
      return p.name.toLowerCase() === locationName.toLowerCase();
    });
  }

}

